import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useCameraRecovery } from '@renderer/pages/View'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay } from '@renderer/utils/lutUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { useTranslation } from 'react-i18next'
import i18n from '@renderer/i18n'
import Button from '@renderer/components/bahela/Button'
import cogoToast from '@successtar/cogo-toast'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useCachedImage } from '@renderer/hooks/useCachedImage'

interface UserContext {
  user?: {
    email: string
  }
}

const CapturePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const cameraRecoveryContext = useCameraRecovery()
  const {
    setSessionState,
    userPreferences,
    id,
    gallery,
    galleryKey,
    uploadSessionState,
    logActivity
  } = useSessionStateStore()
  const { config: globalConfig, getFilter, filters, getFrame } = useConfigStore()
  const config = globalConfig?.pages.capture
  const [capturedImageURL, setCapturedImageURL] = useState<string | null>(null)
  const [prepCountdown, setPrepCountdown] = useState<string>('00:00')
  const uniquePhotoTypes = new Set(
    getFrame(userPreferences.frameId || '')
      ?.drawParams.images.map((image) => image.type)
      .filter((type) => type.startsWith('photo-'))
  )
  const uniquePhotoCount = uniquePhotoTypes.size
  const initialCaptureCountdown = config?.settings.captureCountdown || 3
  const [captureCountdown, setCaptureCountdown] = useState<number>(initialCaptureCountdown)
  const [preparing, setPreparing] = useState(userPreferences.retakeCount === 0)
  const [capturing, setCapturing] = useState(false)
  const [sourceRatio, setSourceRatio] = useState<number | null>(null)
  const [isCapturingWithShutter, setIsCapturingWithShutter] = useState(false)
  const [isCaptureError, setIsCaptureError] = useState(false)
  const [isRecoveringCamera, setIsRecoveringCamera] = useState(false)
  const [isPreviewPaused, setIsPreviewPaused] = useState(false)
  const isRecoveringRef = useRef(false) // Ref to check recovery state in callbacks
  const previewReadyAfterRecoveryRef = useRef<boolean>(false) // Track preview readiness after recovery

  const prepTime = userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
  const captureTime = config?.settings.captureCountdown || 3

  const prepCountdownHook = useCountdown(prepTime)
  const captureCountdownHook = useCountdown(captureTime)

  const stopCaptureRef = useRef<boolean>(false)

  const defaultCaptureCount = 3
  const captureCount = Math.max(
    uniquePhotoCount,
    config?.settings.captureCount || defaultCaptureCount
  )
  const remainingCapturesRef = useRef<number>(
    userPreferences.retakeCount > 0 ? userPreferences.retakeCount : captureCount
  )
  const selectedFilter = getFilter(userPreferences.filterId || filters[0]._id)
  const { downloadLocal, cameraSettings } = useSettingsStore()
  const { fetchSubscriptionAndCredits } = useCreditsStore()

  // Helper function to normalize captureRatio - 'auto' means no cropping
  const getNormalizedCaptureRatio = (): number => {
    if (cameraSettings.captureRatio === 'auto') return 4 / 3 // Default to 4:3 for auto
    if (typeof cameraSettings.captureRatio === 'number') return cameraSettings.captureRatio
    return 4 / 3 // Default fallback
  }

  const displayRatio = userPreferences.framePhotoRatio || getNormalizedCaptureRatio()
  const canvas = document.querySelector('canvas')
  const canvasContext = canvas?.getContext('bitmaprenderer')
  const context = useOutletContext<UserContext | null>()

  const useRecording = userPreferences.useRecording
  const recordingSpeed = globalConfig?.pages.capture.settings.recordingSpeed || 1
  const targetDuration = (globalConfig?.pages.capture.settings.captureCountdown || 3) * 1000
  const FPS = 30
  const totalFrames = Math.ceil((targetDuration * FPS) / 1000)

  // Initialize recorders after canvas is ready
  const sessionCanvasRecorderRef = useRef<Recorder | null>(null)
  const captureCanvasRecorderRef = useRef<Recorder | null>(null)

  const validateDimensions = (width: number, height: number): { width: number; height: number } => {
    const validatedWidth = Math.floor(width / 2) * 2
    const validatedHeight = Math.floor(height / 2) * 2
    return { width: validatedWidth, height: validatedHeight }
  }

  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  useEffect(() => {
    if (canvasContext && canvas && canvasDimensions) {
      const validatedDimensions = validateDimensions(canvas?.width || 720, canvas?.height || 720)

      if (userPreferences.recordingType === 'session_recording') {
        const newSessionRecorder = new Recorder(canvasContext as RenderingContext, {
          name: `dreambooth-${id}-session`,
          encoderOptions: {
            codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
            width: validatedDimensions.width,
            height: validatedDimensions.height,
            bitrate: 2000000
          },
          duration: Infinity,
          download: downloadLocal && userPreferences.recordingType === 'session_recording',
          frameRate: FPS
        })
        sessionCanvasRecorderRef.current = newSessionRecorder
      }

      if (
        userPreferences.recordingType === 'strip_live' ||
        userPreferences.recordingType === 'strip_story'
      ) {
        const newCaptureRecorder = new Recorder(canvasContext as RenderingContext, {
          name: `dreambooth-${id}-capture`,
          encoderOptions: {
            codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
            width: validatedDimensions.width,
            height: validatedDimensions.height,
            bitrate: 2000000
          },
          duration: Infinity,
          download:
            downloadLocal &&
            (userPreferences.recordingType === 'strip_live' ||
              userPreferences.recordingType === 'strip_story'),
          frameRate: FPS
        })
        captureCanvasRecorderRef.current = newCaptureRecorder
      }
    }

    return (): void => {
      sessionCanvasRecorderRef.current?.stop()
      captureCanvasRecorderRef.current?.stop()
    }
  }, [canvasContext, canvas, canvasDimensions])

  // Load image and get its ratio when capturedImageURL changes
  useEffect(() => {
    if (capturedImageURL) {
      const img = new Image()
      img.onload = (): void => {
        setSourceRatio(img.width / img.height)
      }
      img.src = capturedImageURL
    }
  }, [capturedImageURL])

  // Calculate image style based on source and display ratios
  const getImageStyle = (): React.CSSProperties => {
    if (!sourceRatio) return { visibility: 'hidden' as const }

    return {
      width: sourceRatio > displayRatio ? `${(sourceRatio / displayRatio) * 100}%` : '100%',
      height: sourceRatio > displayRatio ? '100%' : `${(displayRatio / sourceRatio) * 100}%`,
      objectFit: 'cover' as const,
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    }
  }

  // Container style maintains the display ratio
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '200px',
    aspectRatio: `${displayRatio}`,
    overflow: 'hidden'
  }

  useEffect(() => {
    // Upload session state on mount
    uploadSessionState()

    // Set up prep countdown
    prepCountdownHook.setOnTick(() => {
      setPrepCountdown(prepCountdownHook.getFormattedRemainingSeconds())
    })

    prepCountdownHook.setOnComplete(() => {
      // Log prep countdown timeout
      logActivity('Prep countdown timed out', {
        type: 'prep',
        duration: prepTime,
        isRetake: userPreferences.retakeCount > 0
      })
      startCaptureSession()
    })

    // Set up capture countdown
    captureCountdownHook.setOnTick(() => {
      // Don't update if recovery is in progress
      if (isRecoveringRef.current) {
        return
      }
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
      setCapturing(true)
    })

    captureCountdownHook.setOnComplete(() => {
      // Don't capture if recovery is in progress
      if (isRecoveringRef.current) {
        console.log('[COUNTDOWN] Recovery in progress, skipping capture from global callback')
        return
      }
      // Log capture countdown timeout
      logActivity('Capture countdown timed out', {
        type: 'capture',
        duration: captureTime,
        remainingCaptures: remainingCapturesRef.current,
        isRetake: userPreferences.retakeCount > 0
      })
      handleCapture()
    })

    // Handle auto-start when prepTimeout is null/0 (not a retake)
    const prepTimeout = config?.settings.prepTimeout
    const isRetake = userPreferences.retakeCount > 0
    if ((prepTimeout === null || prepTimeout === 0 || prepTimeout === undefined) && !isRetake) {
      // Auto-start immediately - this will trigger credit deduction in startCaptureSession()
      startCaptureSession()
    } else {
      prepCountdownHook.start()
    }

    return (): void => {
      sessionCanvasRecorderRef.current?.stop()
      captureCanvasRecorderRef.current?.stop()
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      stopCaptureRef.current = true // Signal the runCaptureLoop to stop
    }
  }, [])

  // Monitor preview readiness after recovery
  useEffect(() => {
    if (isRecoveringRef.current && canvasDimensions) {
      console.log('[RECOVERY] Preview is ready after recovery')
      previewReadyAfterRecoveryRef.current = true
    }
  }, [canvasDimensions])

  const startCaptureSession = async (): Promise<void> => {
    stopCaptureRef.current = false // Reset the stopCaptureRef
    
    // Deduct credits only on first capture (not retakes)
    const isFirstCapture = userPreferences.retakeCount === 0
    if (isFirstCapture) {
      // Try to fetch subscription/credits to refresh auth tokens, but don't block session start
      // If offline or fetch fails, proceed anyway - session will queue for upload when online
      const isOnline = navigator.onLine
      if (isOnline) {
        // Online - try to fetch but don't block if it fails
        try {
          await Promise.race([
            fetchSubscriptionAndCredits(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
          ])
        } catch (error) {
          // If fetch fails or times out, log but don't block session start
          console.warn('[CAPTURE] Failed to fetch subscription/credits, proceeding anyway:', error)
        }
      } else {
        // Offline - start fetchSubscriptionAndCredits in background but don't wait
        fetchSubscriptionAndCredits().catch((error) => {
          console.warn('[CAPTURE] Background fetchSubscriptionAndCredits failed:', error)
        })
      }

      // Upload session state to trigger backend credit deduction
      // Backend will check if session exists, if user has subscription, etc.
      setTimeout(() => {
        uploadSessionState()
      }, 2000)
    }

    // Log processing start
    logActivity('Capture session started!', {
      isRetake: userPreferences.retakeCount > 0,
      useRecording: useRecording,
      captureTime,
      prepTime
    })

    try {
      prepCountdownHook.stop()
      setPreparing(false)
      setCapturing(true)

      // Start session recording if enabled
      if (useRecording && sessionCanvasRecorderRef.current) {
        try {
          await sessionCanvasRecorderRef.current.start()
        } catch (error) {
          console.error('Error starting session recorder', error)
          // Log recording failure
          logActivity('Error starting session recorder', {
            error:
              error instanceof Error
                ? error.message
                : t('capture.errorStartingSessionRecorder', 'Error starting session recorder'),
            component: 'session_recorder'
          })
        }
      }

      // Log processing complete
      logActivity('Capture session processing completed', {
        isRetake: userPreferences.retakeCount > 0,
        useRecording: useRecording
      })

      runCaptureLoop()
    } catch (error) {
      console.error('Error in startCaptureSession:', error)
      // Log processing failure
      logActivity('Capture session processing failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        isRetake: userPreferences.retakeCount > 0
      })
    }
  }

  const stopRecording = async (): Promise<void> => {
    if (useRecording && sessionCanvasRecorderRef.current) {
      const recordedChunks = await sessionCanvasRecorderRef.current.stop()

      const mp4Blob = new Blob([recordedChunks as ArrayBuffer], { type: 'video/mp4' })
      const mp4Url = URL.createObjectURL(mp4Blob)

      const videoElement = document.createElement('video')
      videoElement.src = mp4Url
      videoElement.controls = true

      const videoFile = new File([mp4Blob], `dreambooth-${id}-session.mp4`, { type: 'video/mp4' })

      // Start video processing in background
      backgroundVideoProcessor.processVideo({
        id: `session-${id}-${Date.now()}`,
        type: 'session',
        resources: [], // Not needed for capture videos
        frameConfig: {
          id: 'capture-video',
          baseImage: '',
          drawParams: {
            canvasWidth: 1200,
            canvasHeight: 1800,
            backgroundColor: '#FFFFFF',
            images: []
          }
        },
        sessionId: id || '',
        userId: context?.user?.email || '',
        projectId: projectId || '',
        galleryId: galleryKey || gallery.id,
        videoFile, // Pass the video file for capture videos
        onComplete: (url) => {
          // Update state with video URL when complete
          const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
          setSessionState({
            gallery: {
              capturedItems: [
                ...prevItems,
                { id: 'dreambooth-session-recording-' + new Date().getTime(), recordingUrl: mp4Url }
              ]
            },
            // Log recording upload activity
            logs: [
              {
                message: 'Session recording uploaded',
                details: {
                  type: 'recording',
                  url
                },
                page: 'capture',
                level: 'info' as const,
                timestamp: new Date(),
                sessionId: id || ''
              }
            ]
          })
        },
        onError: (error) => {
          console.error('Error processing session video:', error)
          cogoToast.error(
            t('capture.errorProcessingSessionVideo', 'Error processing session video')
          )
        }
      })
    }
  }

  // Camera recovery callback - called when camera reconnects
  const onCameraReconnected = (): void => {
    console.log('[RECOVERY] Camera reconnected, resuming capture')
    // Clear recovery flags
    isRecoveringRef.current = false
    setIsPreviewPaused(false)
    setIsRecoveringCamera(false)
    setIsCaptureError(false)

    cogoToast.success(
      t('capture.cameraReconnected', 'Camera reconnected successfully. Resuming capture...')
    )

    logActivity('Camera recovery completed, resuming capture', {
      remainingCaptures: remainingCapturesRef.current,
      isRetake: userPreferences.retakeCount > 0
    })

    // Restart countdown from beginning
    const captureTime = config?.settings.captureCountdown || 3
    captureCountdownHook.setRemainingSeconds(captureTime)

    // Note: Capture loop will wait for preview readiness before starting countdown
  }

  // Handle camera recovery when capture fails
  const handleCameraRecovery = async (preservedSelectedCameraParam?: string): Promise<void> => {
    if (isRecoveringCamera) {
      console.log('[RECOVERY] Recovery already in progress')
      return
    }

    console.log('[RECOVERY] Starting camera recovery process')
    // Reset preview ready flag
    previewReadyAfterRecoveryRef.current = false
    // Set recovery flag in ref immediately so callbacks can check it
    isRecoveringRef.current = true
    setIsRecoveringCamera(true)
    setIsPreviewPaused(true)
    setIsCaptureError(false)
    setCapturing(false) // Stop capturing state
    // Stop countdown immediately and clear callbacks to prevent it from continuing
    captureCountdownHook.stop()
    // Clear callbacks to prevent them from firing
    captureCountdownHook.setOnComplete(null)
    captureCountdownHook.setOnTick(null)
    // Reset countdown display
    setCaptureCountdown(initialCaptureCountdown)

    logActivity('Camera recovery process started', {
      remainingCaptures: remainingCapturesRef.current,
      isRetake: userPreferences.retakeCount > 0
    })

    // CRITICAL: Use preserved camera from parameter, or try to get from store
    // The parameter takes precedence because it was preserved before any operations
    const { selectedCamera: selectedCameraId, setSelectedCamera } = useSettingsStore.getState()
    const preservedSelectedCamera = preservedSelectedCameraParam || selectedCameraId
    console.log('[RECOVERY] Preserved selectedCamera:', preservedSelectedCamera)

    // CRITICAL: Restore selectedCamera immediately if it was cleared by error handlers
    // This ensures recovery can proceed even if uniCamUtils cleared it during capture failure
    if (preservedSelectedCamera && !selectedCameraId) {
      console.log(
        '[RECOVERY] Restoring selectedCamera that was cleared by error handlers:',
        preservedSelectedCamera
      )
      setSelectedCamera(preservedSelectedCamera)
    }

    if (!preservedSelectedCamera) {
      console.error('[RECOVERY] No selectedCamera found, cannot recover')
      cogoToast.error(
        t(
          'capture.recoveryNoCamera',
          'No camera selected. Please select a camera in settings and try again.'
        ),
        { hideAfter: 5 }
      )
      setIsRecoveringCamera(false)
      setIsPreviewPaused(false)
      return
    }

    try {
      // Strategy: Try simple reconnect first (no disconnect)
      console.log('[RECOVERY] Attempting simple reconnect first (no disconnect)')
      cogoToast.info(t('capture.recoveryReconnecting', 'Reconnecting camera...'))

      try {
        // Ensure selectedCamera is set before attempting reconnect
        // (it might have been cleared by error handlers)
        if (!useSettingsStore.getState().selectedCamera && preservedSelectedCamera) {
          console.log(
            '[RECOVERY] Restoring selectedCamera before reconnect:',
            preservedSelectedCamera
          )
          setSelectedCamera(preservedSelectedCamera)
        }

        // Try to reconnect without disconnecting first
        let deviceType: 'webcam' | 'camera' = 'webcam'
        let params: string | { productId?: number; vendorId?: number } = preservedSelectedCamera

        if (!isNaN(Number(preservedSelectedCamera))) {
          deviceType = 'camera'
          params = { productId: Number(preservedSelectedCamera) }
        } else if (preservedSelectedCamera === 'detect') {
          deviceType = 'camera'
          params = {}
        }

        // Try quick reconnect with timeout (5 seconds max)
        const reconnectPromise = unifiedCameraUtils.tryToConnect(deviceType, params, 'primary')
        const timeoutPromise = new Promise((_resolve, reject) => {
          setTimeout(() => {
            reject(new Error('Reconnection timed out'))
          }, 5000)
        })

        await Promise.race([reconnectPromise, timeoutPromise])

        console.log('[RECOVERY] Simple reconnect successful!')
        cogoToast.success(t('capture.recoveryReconnected', 'Camera reconnected!'), { hideAfter: 2 })

        // Success! Resume capture
        onCameraReconnected()
        return
      } catch (reconnectError) {
        console.log(
          '[RECOVERY] Simple reconnect failed, will disconnect and reconnect:',
          reconnectError
        )
        // Continue to disconnect + reconnect flow
      }

      // If simple reconnect failed, disconnect and reconnect
      console.log('[RECOVERY] Disconnecting camera before reconnection')
      cogoToast.info(t('capture.recoveryDisconnecting', 'Disconnecting camera...'), {
        hideAfter: 2
      })

      try {
        await new Promise((resolve) => setTimeout(resolve, 500))
        await unifiedCameraUtils.disconnect('primary')
        console.log('[RECOVERY] Camera disconnected')
      } catch (disconnectError) {
        console.warn('[RECOVERY] Camera disconnect had issues, continuing:', disconnectError)
      }

      // CRITICAL: Restore selectedCamera immediately after disconnect
      // (uniCamUtils error handlers might have cleared it)
      if (!useSettingsStore.getState().selectedCamera && preservedSelectedCamera) {
        console.log(
          '[RECOVERY] Restoring selectedCamera after disconnect:',
          preservedSelectedCamera
        )
        setSelectedCamera(preservedSelectedCamera)
      }

      // Reduced wait time before showing CameraPrompt
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Trigger CameraPrompt via recovery context
      if (cameraRecoveryContext?.onCameraRecoveryRequest) {
        console.log('[RECOVERY] Triggering CameraPrompt for user reconnection')
        cameraRecoveryContext.onCameraRecoveryRequest(onCameraReconnected)
      } else {
        console.error('[RECOVERY] onCameraRecoveryRequest not available in context')
        cogoToast.error(
          t(
            'capture.recoveryContextError',
            'Recovery system error. Please reconnect camera manually.'
          )
        )
        // Fallback: try to reconnect directly
        setIsRecoveringCamera(false)
        setIsPreviewPaused(false)
      }
    } catch (error) {
      console.error('[RECOVERY] Error during camera recovery:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      cogoToast.error(
        t('capture.recoveryError', 'Camera recovery failed. Please reconnect manually.'),
        { hideAfter: 5 }
      )
      logActivity(`Camera recovery error: ${errorMessage}`, {
        error: errorMessage,
        remainingCaptures: remainingCapturesRef.current,
        level: 'error'
      })
      // Still try to show CameraPrompt as fallback
      if (cameraRecoveryContext?.onCameraRecoveryRequest) {
        cameraRecoveryContext.onCameraRecoveryRequest(onCameraReconnected)
      } else {
        // If context is not available, reset state
        setIsRecoveringCamera(false)
        setIsPreviewPaused(false)
      }
    }
  }

  const handleCapture = async (): Promise<string | undefined> => {
    // CRITICAL: Preserve selectedCamera BEFORE any camera operations
    // uniCamUtils error handlers might clear it, so we preserve it early
    const { selectedCamera: selectedCameraId } = useSettingsStore.getState()
    const preservedSelectedCamera = selectedCameraId

    try {
      // Log capture processing start
      logActivity('Capture processing started', {
        remainingCaptures: remainingCapturesRef.current,
        isRetake: userPreferences.retakeCount > 0
      })

      // Stats tracked by Gallery API

      flashEffect('.flash')
      setCapturing(false)
      setIsCaptureError(false)

      if (cameraSettings.useShutterCapture && useCameraStore.getState().deviceType == 'camera') {
        setIsCapturingWithShutter(true)
      }

      await new Promise((resolve) => setTimeout(resolve, 100))
      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        applyFilter,
        undefined,
        !cameraSettings.useShutterCapture
      )

      setIsCapturingWithShutter(false)

      if (imageData) {
        const imageUrl = URL.createObjectURL(imageData)
        setCapturedImageURL(imageUrl)

        // Download photo immediately if downloadLocal is true
        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${id}-${Date.now()}.jpg`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)

          // Log photo download activity
          logActivity('Photo downloaded locally', {
            type: 'photo',
            filename: `dreambooth-${id}-${Date.now()}.jpg`
          })
        }

        remainingCapturesRef.current--
        return imageUrl
      }

      // Check if this is a camera hardware failure (shutter mode, camera device)
      const deviceType = useCameraStore.getState().deviceType
      const isCameraHardwareFailure =
        cameraSettings.useShutterCapture && deviceType === 'camera' && !imageData

      if (isCameraHardwareFailure) {
        console.log('[CAPTURE] Camera hardware failure detected, triggering recovery')
        // Show capture failure message FIRST - user needs to know what happened
        cogoToast.error(
          t('capture.captureFailed', 'Capture failed. Attempting to recover camera...'),
          { hideAfter: 4 }
        )
        logActivity('Camera hardware failure detected, starting recovery process.', {
          error: 'No image data captured',
          remainingCaptures: remainingCapturesRef.current,
          isRetake: userPreferences.retakeCount > 0,
          level: 'error',
          recovery: true
        })
        // Small delay to let user see the error message, then start recovery
        await new Promise((resolve) => setTimeout(resolve, 500))
        // Trigger camera recovery with preserved camera
        handleCameraRecovery(preservedSelectedCamera)
        return undefined
      }

      // Log processing failure if no image data (but not camera hardware failure)
      logActivity('Capture processing failed: No image data captured.', {
        error: 'No image data captured',
        remainingCaptures: remainingCapturesRef.current,
        isRetake: userPreferences.retakeCount > 0,
        level: 'error'
      })

      setIsCaptureError(true)
      return undefined
    } catch (error) {
      console.error('Capture failed:', error)
      setCapturing(false)
      setIsCapturingWithShutter(false)

      // Check if this is a camera hardware failure
      const deviceType = useCameraStore.getState().deviceType
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      const isCameraHardwareFailure =
        cameraSettings.useShutterCapture &&
        deviceType === 'camera' &&
        (errorMessage.includes('LIBUSB') ||
          errorMessage.includes('camera') ||
          errorMessage.includes('device') ||
          errorMessage.includes('USB'))

      if (isCameraHardwareFailure) {
        console.log('[CAPTURE] Camera hardware failure detected in catch, triggering recovery')
        // Show capture failure message FIRST - user needs to know what happened
        cogoToast.error(
          t('capture.captureFailed', 'Capture failed. Attempting to recover camera...'),
          { hideAfter: 4 }
        )
        logActivity(`Camera hardware failure detected: ${errorMessage}`, {
          error: errorMessage,
          remainingCaptures: remainingCapturesRef.current,
          isRetake: userPreferences.retakeCount > 0,
          level: 'error',
          recovery: true
        })
        // Small delay to let user see the error message, then start recovery
        await new Promise((resolve) => setTimeout(resolve, 500))
        // Trigger camera recovery with preserved camera
        handleCameraRecovery(preservedSelectedCamera)
        return undefined
      }

      // Log processing failure (not camera hardware failure)
      logActivity(`Capture processing failed: ${errorMessage}`, {
        error: errorMessage,
        remainingCaptures: remainingCapturesRef.current,
        isRetake: userPreferences.retakeCount > 0,
        level: 'error'
      })

      setIsCaptureError(true)
      return undefined
    }
  }

  const runCaptureLoop = async (): Promise<void> => {
    console.log('Starting capture loop with remaining captures:', remainingCapturesRef.current)
    while (remainingCapturesRef.current > 0 && !stopCaptureRef.current) {
      // Wait for camera recovery to complete if in progress
      if (isRecoveringRef.current || isRecoveringCamera) {
        console.log('[CAPTURE LOOP] Waiting for camera recovery to complete...')
        const recoveryStartTime = Date.now()

        // A PAID session must never be silently dumped back to Welcome on a camera hiccup —
        // that loses a session the customer already paid for. Hold indefinitely while the
        // recovery CameraPrompt stays up so an operator can reconnect and resume; only an
        // explicit quit (stopCaptureRef, via QuitModal -> unmount) ends it. UNPAID sessions
        // keep the original 2-minute give-up -> home.
        const isPaidSession =
          useSessionStateStore.getState().transaction?.status === 'settlement'
        const maxRecoveryWait = isPaidSession ? Number.POSITIVE_INFINITY : 120000
        let notifiedPaidHold = false

        while (
          (isRecoveringRef.current || isRecoveringCamera) &&
          !stopCaptureRef.current &&
          Date.now() - recoveryStartTime < maxRecoveryWait
        ) {
          // Once the wait drags on for a paid session, surface a clear, non-dismissing
          // operator message so the booth isn't silently stuck with no explanation.
          if (isPaidSession && !notifiedPaidHold && Date.now() - recoveryStartTime > 120000) {
            notifiedPaidHold = true
            cogoToast.error(
              t(
                'capture.recoveryHoldPaid',
                'Camera issue — your paid session is saved. Please ask an operator for help.'
              ),
              { hideAfter: 0 }
            )
            logActivity('Camera recovery stalled on PAID session - holding for operator', {
              remainingCaptures: remainingCapturesRef.current,
              level: 'error'
            })
          }
          await new Promise((resolve) => setTimeout(resolve, 500))
        }

        // Explicit quit during recovery — stop cleanly (QuitModal owns session reset + nav).
        if (stopCaptureRef.current) {
          return
        }

        if (isRecoveringRef.current || isRecoveringCamera) {
          // Only reachable for UNPAID sessions (paid sessions wait indefinitely above).
          console.error('[CAPTURE LOOP] Camera recovery timeout after 2 minutes')
          cogoToast.error(
            t('capture.recoveryTimeout', 'Camera recovery timed out. Returning to home page.')
          )
          logActivity('Camera recovery timeout - navigating home', {
            remainingCaptures: remainingCapturesRef.current,
            level: 'error'
          })
          // Navigate home on timeout
          navigate(`/${projectId}`)
          return
        }

        // Recovery completed, wait for preview to be ready
        console.log('[CAPTURE LOOP] Recovery completed, waiting for preview readiness...')
        const previewReadyStartTime = Date.now()
        const maxPreviewWait = 3000 // 3 seconds max for preview

        while (
          !previewReadyAfterRecoveryRef.current &&
          Date.now() - previewReadyStartTime < maxPreviewWait
        ) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        if (!previewReadyAfterRecoveryRef.current) {
          console.warn('[CAPTURE LOOP] Preview readiness timeout, continuing anyway')
        }

        // Add 1 second delay for UX (let user see preview is back)
        console.log('[CAPTURE LOOP] Preview ready, adding 1s delay for UX')
        await new Promise((resolve) => setTimeout(resolve, 1000))

        // Reset preview ready flag for next potential recovery
        previewReadyAfterRecoveryRef.current = false
      }

      let frameCount = 0
      const startTime = performance.now()
      const captureId = id + '-' + Date.now()
      let imageUrl: string | undefined = undefined
      let captureUrl: string | undefined = undefined

      console.log('Starting new capture iteration:', {
        remainingCaptures: remainingCapturesRef.current,
        useRecording,
        hasRecorder: !!captureCanvasRecorderRef.current,
        isRecovering: isRecoveringCamera
      })

      // Start capture recording if enabled
      if (useRecording && captureCanvasRecorderRef.current) {
        try {
          await captureCanvasRecorderRef.current.start()
          console.log('Successfully started capture recorder')
        } catch (error) {
          cogoToast.error('Error starting video recorder')
          console.error('Error starting capture recorder', error)
        }
      }

      // Check if recovery started before starting countdown
      if (isRecoveringRef.current || isRecoveringCamera) {
        console.log('[CAPTURE LOOP] Recovery in progress, skipping this capture iteration')
        continue
      }

      // Start the countdown and wait for capture
      console.log('Attempting to start countdown')

      // Set capturing state to true so countdown is visible
      setCapturing(true)
      captureCountdownHook.start()
      // Check again immediately after starting (recovery might have started)
      if (isRecoveringRef.current || isRecoveringCamera) {
        console.log('[CAPTURE LOOP] Recovery started right after countdown start, stopping')
        captureCountdownHook.stop()
        captureCountdownHook.setOnComplete(null)
        setCapturing(false)
        continue
      }

      const stepInterval = 1000 / (FPS / recordingSpeed)
      let sessionStepLoop: NodeJS.Timeout | null = null

      // Start session recording step loop if enabled
      if (useRecording && sessionCanvasRecorderRef.current) {
        sessionStepLoop = setInterval(async () => {
          if (sessionCanvasRecorderRef.current?.status !== RecorderStatus.Recording) return
          try {
            await sessionCanvasRecorderRef.current?.step()
          } catch (error) {
            console.error('Error stepping session recorder', error)
          }
        }, stepInterval)
      }

      // Create a promise for capture completion
      const captureCompletionPromise = new Promise<void>((resolve) => {
        captureCountdownHook.setOnComplete(async (): Promise<void> => {
          // Check if recovery started during countdown
          if (isRecoveringRef.current || isRecoveringCamera) {
            console.log('[CAPTURE LOOP] Recovery in progress, skipping capture')
            resolve()
            return
          }
          imageUrl = await handleCapture()
          // Check again after capture (recovery might have started during capture)
          if (isRecoveringRef.current || isRecoveringCamera) {
            console.log('[CAPTURE LOOP] Recovery started during capture, skipping result')
            resolve()
            return
          }
          resolve()
        })
      })

      // Record frames during countdown if recording is enabled
      if (useRecording && captureCanvasRecorderRef.current) {
        // Instead of setInterval, use a time-based loop
        while (frameCount < totalFrames) {
          if (captureCanvasRecorderRef.current.status !== RecorderStatus.Recording) break

          const currentTime = performance.now()
          const elapsedTime = currentTime - startTime
          const targetFrameTime = (frameCount * 1000) / FPS

          if (elapsedTime >= targetFrameTime) {
            try {
              await captureCanvasRecorderRef.current.step()
              frameCount++
            } catch (error) {
              console.error('Error stepping recorders', error)
            }
          }

          // Small delay to prevent CPU overload
          await new Promise((resolve) => setTimeout(resolve, 1))
        }
      }

      // Wait for capture to complete
      await captureCompletionPromise
      // Check if recovery started during capture
      if (isRecoveringRef.current || isRecoveringCamera) {
        console.log('[CAPTURE LOOP] Recovery started, stopping this iteration')
        if (sessionStepLoop) {
          clearInterval(sessionStepLoop)
        }
        // Stop any recording that might be in progress
        if (useRecording && captureCanvasRecorderRef.current) {
          try {
            await captureCanvasRecorderRef.current.stop()
          } catch (error) {
            console.error('Error stopping recorder during recovery:', error)
          }
        }
        continue // Skip to next iteration (which will wait for recovery)
      }
      if (sessionStepLoop) {
        clearInterval(sessionStepLoop)
      }

      // Stop and save capture recording if enabled
      if (useRecording && captureCanvasRecorderRef.current) {
        try {
          const captureChunks = await captureCanvasRecorderRef.current.stop()
          const captureBlob = new Blob([captureChunks as ArrayBuffer], { type: 'video/mp4' })
          captureUrl = URL.createObjectURL(captureBlob)
        } catch (error) {
          console.error('Error stopping capture recorder', error)
        }
      }

      if (imageUrl) {
        // Get current gallery state
        const currentGallery = useSessionStateStore.getState().gallery
        const prevItems = currentGallery.capturedItems || []

        // Update gallery state with new photo
        setSessionState({
          gallery: {
            capturedItems: [
              ...prevItems,
              {
                id: captureId,
                photoUrl: imageUrl,
                recordingUrl: captureUrl || '',
                timestamp: new Date()
              }
            ]
          }
        })

        // Log capture completion activity
        logActivity('Photo captured successfully', {
          id: captureId,
          hasPhoto: !!imageUrl,
          hasRecording: !!captureUrl,
          remainingCaptures: remainingCapturesRef.current,
          isRetake: userPreferences.retakeCount > 0
        })
      }

      // Reset countdown for next capture
      if (remainingCapturesRef.current > 1) {
        console.log('Resetting countdown for next capture')
        const captureTime = config?.settings.captureCountdown || 3
        captureCountdownHook.setRemainingSeconds(captureTime)
      } else {
        console.log('Not resetting countdown - no more captures remaining')
      }
    }

    // Stop and save session recording if enabled
    if (useRecording && sessionCanvasRecorderRef.current) {
      try {
        await stopRecording()
      } catch (error) {
        console.error('Error stopping session recording', error)
      }
    }

    // Upload session state before navigating
    uploadSessionState()

    // Only navigate if the session was not cancelled
    if (stopCaptureRef.current) return

    // Check if retakes are still allowed
    const maxRetakeSession = globalConfig?.pages.retake?.settings?.maxRetakeSession
    const retakeSessionCount = userPreferences.retakeSessionsCompleted || 0

    const canRetake =
      maxRetakeSession === undefined ||
      maxRetakeSession === null ||
      retakeSessionCount < maxRetakeSession

    if (canRetake) {
      navigate(`/${projectId}/retake`)
    } else {
      navigate(`/${projectId}/frame`)
    }
  }

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (selectedFilter?.adjustments) {
      filterUtils.applyAdjustments(ctx, selectedFilter.adjustments)
    }
  }

  // Display progress
  const captureProgress = {
    current:
      userPreferences.retakeCount > 0
        ? userPreferences.retakeCount - remainingCapturesRef.current
        : captureCount - remainingCapturesRef.current,
    total: userPreferences.retakeCount > 0 ? userPreferences.retakeCount : captureCount
  }

  // Call hook at top level - must be before any conditional returns
  const cachedCapturedImageURL = useCachedImage(capturedImageURL || '')

  return (
    <div className="relative w-full h-full bg-carbon">
      <div id="flash" className="w-full h-full flash"></div>

      {/* Shutter capture message */}
      {isCapturingWithShutter && (
        <div className="absolute inset-0 bg-white/50 z-[5] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-black mb-4">
              📸 {t('capture.lookAtCamera', 'Look at the Camera')}
            </h2>
            <p className="text-xl text-black/80">
              {t('capture.stayStill', 'Please stay still while we capture your photo...')}
            </p>
          </div>
        </div>
      )}

      {/* Capture error message - only show for non-connection errors */}
      {isCaptureError && (
        <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-red-500 mb-4">
              {t('capture.captureFailed', 'Capture Failed')}
            </h2>
            <p className="text-xl text-white/80 mb-6">
              {t('capture.stayStillCapturing', 'Please stay still when capturing')}
            </p>
            <p className="text-lg text-white/60">
              {t('capture.retryingMoment', 'Retrying in a moment...')}
            </p>
          </div>
        </div>
      )}

      <div className="absolute bottom-24 right-12 flex flex-col items-center gap-4 z-10">
        <div className="bg-white/10 p-2 rounded-lg">
          {capturedImageURL ? (
            <div style={containerStyle}>
              <img
                src={cachedCapturedImageURL}
                alt={t('capture.lastCaptured', 'Last captured')}
                style={getImageStyle()}
                crossOrigin="anonymous"
              />
            </div>
          ) : (
            <div
              style={{
                width: 200 * (typeof displayRatio === 'number' ? displayRatio : 4 / 3),
                height: 200
              }}
              className="flex items-center justify-center bg-black/30"
            >
              <span className="text-white/50 text-xl">
                {t('capture.noPhotoYet', 'No photo yet')}
              </span>
            </div>
          )}
        </div>
        <span className=" text-white text-5xl font-serif-bahela">
          {captureProgress.current}/{captureProgress.total}
        </span>
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
        {preparing && (
          <div className="w-full h-full absolute top-0 left-0 z-[5] bg-black/50 flex flex-col text-white justify-center gap-8 items-center">
            <h1 className="text-[90px] drop-shadow-2xl text-center flex flex-col gap-2 text-white font-serif-bahela leading-none">
              <span>{i18n.language === 'id' ? 'Waktunya' : 'Prepare'}</span>
              <span>{i18n.language === 'id' ? 'bersiap,' : 'Yourself!'}</span>
              {i18n.language === 'id' && <span>Tuan Nona!</span>}
            </h1>
            <span className="font-body-bahela text-[55px] drop-shadow-2xl">{prepCountdown}</span>
            <Button
              onClick={startCaptureSession}
              className="disabled:opacity-50 text-[50px]"
              textColor="methadone"
              disabled={!canvasDimensions}
            >
              {i18n.language === 'id' ? 'Mulai Foto' : 'Start Capturing'}
            </Button>
          </div>
        )}

        {capturing && (
          <div className="absolute inset-0 z-[4] flex items-center justify-center">
            <span
              key={captureCountdown}
              className="text-[348px] text-white animate-countdown drop-shadow-lg"
            >
              {captureCountdown}
            </span>
          </div>
        )}

        <Preview
          key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${getNormalizedCaptureRatio()}-${displayRatio}`}
          adjustments={selectedFilter?.adjustments}
          filter={selectedFilter?._id}
          postLUTAdjustments={selectedFilter?.postLUTAdjustments}
          ratio={displayRatio}
          onCanvasReady={setCanvasDimensions}
          cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
          isPaused={isPreviewPaused}
        />
      </div>

      {userPreferences.useRecording && !preparing && (
        <div className="absolute top-12 left-12 z-[10] flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-500 text-[24px]">{t('capture.recording', 'REC')}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default CapturePage
